'use client'

/**
 * Purpose: Hidden file input + image file to base64 for camera composers.
 * Inputs: onPick callback, optional validateBase64.
 * Outputs: inputRef, open, handleChange.
 */

import { useCallback, useRef } from 'react'
import { fileToBase64 } from '@lib/image-base64'

// Callbacks and optional size validation for picked image files.
export type UseImageFilePickerOptions = {
  onPick: (base64: string) => void | Promise<void>
  onError?: (err: unknown) => void
  validateBase64?: (base64: string) => boolean
  onValidationError?: () => void
}

// Hidden file input helper for camera upload buttons.
export function useImageFilePicker({
  onPick,
  onError,
  validateBase64,
  onValidationError,
}: UseImageFilePickerOptions) {
  const inputRef = useRef<HTMLInputElement>(null)

  // Programmatically open the hidden file input from a button click.
  const open = useCallback(() => {
    inputRef.current?.click()
  }, [])

  // Read selected image to base64, validate size, then forward to parent.
  const handleChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      e.target.value = ''
      if (!file || !file.type.startsWith('image/')) return
      try {
        const base64 = await fileToBase64(file)
        // Reject oversize images before parent onPick runs.
        if (validateBase64 && !validateBase64(base64)) {
          onValidationError?.()
          return
        }
        await onPick(base64)
      } catch (err) {
        onError?.(err)
      }
    },
    [onError, onPick, onValidationError, validateBase64],
  )

  return { inputRef, open, handleChange }
}
